import { http } from '@/api/client/httpClient';
import type { Select2Option } from '@/components/forms/Select2';
import type { Select2Page } from '@/components/forms/Select2Async';

// Finkit.Web FilterDataAppService + GroupProfileAppService. ABP strips the "Get"
// verb prefix from routes (GetEnabledConnectors -> /enabled-connectors).
const BASE = '/api/app/filter-data';
const GP_BASE = '/api/app/group-profile';
const PAGE = { skipCount: 0, maxResultCount: 5000 };

interface ResultDto<T> {
  value: T;
  text: string;
}
interface Paged<T> {
  items: ResultDto<T>[];
  totalCount: number;
}

function toOptions<T extends string | number>(list: ResultDto<T>[]): Select2Option[] {
  return (list ?? []).map((r) => ({ value: r.value as string | number, label: r.text }));
}

async function getList<T extends string | number>(path: string): Promise<Select2Option[]> {
  const { data } = await http.get<ResultDto<T>[]>(`${BASE}/${path}`);
  return toOptions(data);
}

async function getPaged<T extends string | number>(path: string): Promise<Select2Option[]> {
  const { data } = await http.get<Paged<T>>(`${BASE}/${path}`, { params: PAGE });
  return toOptions(data.items);
}

// Server-side paginated + searchable loader for select2 infinite scroll (so large
// lists like groups/instruments/countries don't load all at once).
const PAGE_SIZE = 20;
function pagedSearch(path: string): (term: string, page: number) => Promise<Select2Page> {
  return async (term, page) => {
    const skipCount = (page - 1) * PAGE_SIZE;
    const { data } = await http.get<Paged<string | number>>(`${BASE}/${path}`, {
      params: { search: term, skipCount, maxResultCount: PAGE_SIZE },
    });
    const raw = data.items ?? [];
    return {
      items: raw.map((r) => ({ value: String(r.value), text: r.text })),
      hasMore: skipCount + raw.length < (data.totalCount ?? 0),
    };
  };
}

// Paginated fetchers for the big filter dropdowns (used with Select2Async).
export const filterDataPaged = {
  groups: pagedSearch('groups'),
  instruments: pagedSearch('instruments'),
  countries: pagedSearch('countries'),
  assetClasses: pagedSearch('asset-classes'),
};

// Analytics has its OWN groups/countries endpoints (analytics-filters-preparation).
function analyticsPagedSearch(path: string): (term: string, page: number) => Promise<Select2Page> {
  return async (term, page) => {
    const skipCount = (page - 1) * PAGE_SIZE;
    const { data } = await http.get<Paged<string | number>>(`/api/app/analytics-filters-preparation/${path}`, {
      params: { search: term, skipCount, maxResultCount: PAGE_SIZE },
    });
    const raw = data.items ?? [];
    return {
      items: raw.map((r) => ({ value: String(r.value), text: r.text })),
      hasMore: skipCount + raw.length < (data.totalCount ?? 0),
    };
  };
}
export const analyticsFilterPaged = {
  groups: analyticsPagedSearch('groups'),
  countries: analyticsPagedSearch('countries'),
};

export const filterDataApi = {
  getEnabledConnectors: () => getList<number>('enabled-connectors'),
  getNonTerminalConnectors: () => getList<number>('non-terminal-connectors'),
  getConnectors: () => getList<number>('connectors'),
  getTradeEntryTypes: () => getList<number>('trade-entry-types'),
  getTradeSides: () => getList<number>('trade-sides'),
  getPortfolios: () => getList<number>('portfolios'),
  getMifidClassifications: () => getList<number>('mifid-classifications'),
  getExecutionVenues: () => getList<number>('execution-venues'),
  getGroups: () => getPaged<string>('groups'),
  getNonTerminalGroups: () => getPaged<string>('non-terminal-groups'),
  getInstruments: () => getPaged<string>('instruments'),
  getCountries: () => getPaged<number>('countries'),
  async getGroupProfiles(): Promise<Select2Option[]> {
    // GroupProfilePage.ClientProfitability = 0.
    return filterDataApi.getGroupProfilesForPage(0);
  },
  // GroupProfilePage: ClientProfitability=0, MarketAbuse=1, Sigma=2, Analytics=3, InstrumentProfitability=4.
  async getGroupProfilesForPage(groupProfilePage: number): Promise<Select2Option[]> {
    const { data } = await http.get<{ id: string; name: string }[]>(`${GP_BASE}/for-user`, {
      params: { groupProfilePage },
    });
    return (data ?? []).map((g) => ({ value: g.id, label: g.name }));
  },
};

export const IF_SWAPS_OPTIONS: Select2Option[] = [
  { value: 'Positive', label: 'Positive' },
  { value: 'Negative', label: 'Negative' },
];
